import { Editor, JSONContent } from '@tiptap/core';
import { Blockquote } from '@tiptap/extension-blockquote';
import { Bold } from '@tiptap/extension-bold';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Code } from '@tiptap/extension-code';
import { CodeBlock } from '@tiptap/extension-code-block';
import { Color } from '@tiptap/extension-color';
import { Document } from '@tiptap/extension-document';
import Dropcursor from '@tiptap/extension-dropcursor';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Heading } from '@tiptap/extension-heading';
import { Highlight } from '@tiptap/extension-highlight';
import History from '@tiptap/extension-history';
import { HorizontalRule } from '@tiptap/extension-horizontal-rule';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { ListItem } from '@tiptap/extension-list-item';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import { Strike } from '@tiptap/extension-strike';
import { Text } from '@tiptap/extension-text';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import type { ViewHook } from 'phoenix_live_view';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import MentionsProvider from '../../../services/mentions-provider';
import { MentionSuggestion } from '../../mentions/MentionsList';
import { buildMockMentions } from '../../mentions/mockMentions';
import MentionExtension from '../../mentions/tiptap/extension';
import {
  AlignIcon,
  BoldIcon,
  BulletListIcon,
  ClearFormattingIcon,
  CodeBlockIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  Heading4Icon,
  HeadingIcon,
  HIGHLIGHT_COLOR_OPTIONS,
  ItalicIcon,
  LinkIcon,
  OrderedListIcon,
  QuoteIcon,
  StrikethroughIcon,
  TEXT_ALIGN_OPTIONS,
  TEXT_COLOR_OPTIONS,
  TextIcon,
  UnderlineIcon,
} from './bubbleMenuConfig';
import {
  closeSlashSuggestion,
  createSlashExtension,
  forceRemoveStaleSlashMenuUi,
  getEditorViewSafe,
  hasPendingSlashTrigger,
  replaySlashTokenAtCursor,
  slashCommandsPluginKey,
} from './slash';
import { buildSlashItems, type FieldSuggestion, type FieldTypeSuggestion } from './slashItems';
import type { SlashItem } from './slashTypes';
import { useControlledEditorSync, useHiddenInputSync } from './useExternalValueSync';
import { useLiveViewBridge } from './useLiveViewBridge';
import { useSlashFocusLifecycle } from './useSlashFocusLifecycle';
import { escapeBrackets, extractMentionSlugs, unescapeBrackets } from './utils';

const DEFAULT_PLACEHOLDER = 'Type / for commands…';
const DEFAULT_MENTION_PLACEHOLDER = 'Type @ to mention…';
const LINK_INPUT_PLACEHOLDER = 'Paste or type a link';
const DEBOUNCE_TIMEOUT_MS = 100;
const HEAVY_CONTENT_DEFER_THRESHOLD = 3_000;
const HEAVY_MOUNT_IDLE_DELAY_MS = 160;
const EMPTY_SLASH_ITEMS: SlashItem[] = [];
const DEFAULT_MOCK_MENTION_ROOT_ITEMS = 30;
const DEFAULT_MOCK_MENTION_CHILD_ITEMS = 30;

const LINK_EXTENSION = Link.configure({
  protocols: ['http', 'https', 'mailto', 'tel', 'slack'],
  autolink: true,
  openOnClick: false,
  isAllowedUri: (url, ctx) => {
    const normalizedUrl = MentionExtension.serializeMentions(url || '').trim();
    const urlForValidation = MentionExtension.toSerializedMentionsUrlValidationValue(normalizedUrl);

    if (MentionExtension.hasSerializedMentions(normalizedUrl)) {
      return ctx.defaultValidate(urlForValidation);
    }

    return ctx.defaultValidate(normalizedUrl);
  },
});

const BASE_EXTENSIONS = [Document, Paragraph, Text, History, Dropcursor, Gapcursor, HardBreak];
const RICH_EXTENSIONS = [
  Document,
  Paragraph,
  Text,
  Heading.configure({ levels: [1, 2, 3, 4] }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  CodeBlock,
  HorizontalRule,
  Bold,
  Italic,
  Strike,
  Code,
  Underline,
  TextStyle,
  Color.configure({ types: ['textStyle'] }),
  Highlight.configure({ multicolor: true }),
  LINK_EXTENSION,
  History,
  Dropcursor,
  Gapcursor,
  HardBreak,
];

const isStructurallyEmptyDoc = (doc: ProseMirrorNode) => {
  let hasMeaningfulContent = false;

  doc.descendants(node => {
    if (hasMeaningfulContent) return false;

    if (node.isText) {
      if ((node.text ?? '').trim().length > 0) {
        hasMeaningfulContent = true;
        return false;
      }
      return true;
    }

    if (node.type?.name === 'doc' || node.type?.name === 'paragraph') {
      return true;
    }

    if (node.isAtom || node.isLeaf) {
      hasMeaningfulContent = true;
      return false;
    }

    return true;
  });

  return !hasMeaningfulContent;
};

const extractMentionSlugsFromDoc = (doc: ProseMirrorNode) => {
  const slugs: string[] = [];

  doc.descendants(node => {
    if (node.type?.name === 'mention' && typeof node.attrs?.id === 'string') {
      slugs.push(node.attrs.id);
    }

    return true;
  });

  return Array.from(new Set(slugs)).sort();
};

const getMentionNodeRanges = (doc: ProseMirrorNode) => {
  const ranges: Array<{ from: number; to: number }> = [];

  doc.descendants((node, pos) => {
    if (node.type?.name === 'mention') {
      ranges.push({ from: pos, to: pos + node.nodeSize });
    }

    return true;
  });

  return ranges;
};

const selectionOverlapsMentionNode = (ranges: Array<{ from: number; to: number }>, from: number, to: number) => {
  if (from === to) return false;

  return ranges.some(range => range.from < to && range.to > from);
};

const limitSerializedMentions = (value: string, maxMentions = 1) => {
  if (!value.includes('{{{')) return value;
  if (maxMentions < 1) return value.replace(/\{\{\{(.*?)}}}/g, '');

  let seenMentions = 0;

  return value.replace(/\{\{\{(.*?)}}}/g, match => {
    seenMentions += 1;
    return seenMentions <= maxMentions ? match : '';
  });
};

const normalizeSerializedMentionHrefAttributes = (value: string, maxMentions = 1) =>
  value.replace(/href=(['"])(.*?)\1/gi, (match, quote, href) => {
    if (!href.includes('{{{')) return match;

    const normalizedHref = limitSerializedMentions(href, maxMentions).trim();
    return `href=${quote}${normalizedHref}${quote}`;
  });

type SuperTextHeightCache = Record<string, number>;
type MentionInitCacheMap = Record<
  string,
  { value?: MentionSuggestion[]; promise?: Promise<MentionSuggestion[]>; expiresAt: number }
>;

const getSuperTextHeightCache = (): SuperTextHeightCache => {
  if (typeof window === 'undefined') return {};
  const hostWindow = window as Window & { __superTextHeightCache?: SuperTextHeightCache };
  if (!hostWindow.__superTextHeightCache) {
    hostWindow.__superTextHeightCache = {};
  }
  return hostWindow.__superTextHeightCache;
};

const getMentionInitCache = (): MentionInitCacheMap => {
  if (typeof window === 'undefined') return {};
  const hostWindow = window as Window & { __superTextMentionInitCache?: MentionInitCacheMap };
  if (!hostWindow.__superTextMentionInitCache) {
    hostWindow.__superTextMentionInitCache = {};
  }
  return hostWindow.__superTextMentionInitCache;
};

const loadMentionsCached = (key: string, load: () => Promise<MentionSuggestion[]>, ttlMs = 15_000) => {
  const cache = getMentionInitCache();
  const now = Date.now();
  const entry = cache[key];

  if (entry?.value && entry.expiresAt > now) {
    return Promise.resolve(entry.value);
  }
  if (entry?.promise) {
    return entry.promise;
  }

  const promise = load()
    .then(value => {
      if (Array.isArray(value) && value.length > 0) {
        cache[key] = {
          value,
          expiresAt: Date.now() + ttlMs,
        };
      } else {
        delete cache[key];
      }
      return value;
    })
    .catch(error => {
      delete cache[key];
      throw error;
    });

  cache[key] = { promise, expiresAt: now + ttlMs };
  return promise;
};

const parsePositiveInt = (value: string | null, fallback: number): number => {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getMockMentionsParams = (): { rootCount: number; childCount: number } | null => {
  if (typeof window === 'undefined') return null;

  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isLocalHost) return null;

  const params = new URLSearchParams(window.location.search);
  const isEnabled = ['1', 'true', 'yes'].includes((params.get('mentions_mock') || '').toLowerCase());
  if (!isEnabled) return null;

  return {
    rootCount: parsePositiveInt(params.get('mentions_mock_roots'), DEFAULT_MOCK_MENTION_ROOT_ITEMS),
    childCount: parsePositiveInt(params.get('mentions_mock_children'), DEFAULT_MOCK_MENTION_CHILD_ITEMS),
  };
};

const maybeApplyMockMentions = (suggestions: MentionSuggestion[]): MentionSuggestion[] => {
  const mockParams = getMockMentionsParams();
  if (!mockParams) return suggestions;
  return buildMockMentions(mockParams.rootCount, mockParams.childCount);
};

type OutputFormat = 'json' | 'html';

export type SuperTextHandle = {
  focus: (placement?: Parameters<Editor['commands']['focus']>[0]) => void;
  clearContent: () => void;
  setContent: (serializedValue: string) => void;
  getEditor: () => Editor | null;
};

type EventHandler = string | (() => void);
type ChangeHandler = string | ((value: string) => void);
type PushEventToReply = Parameters<ViewHook['pushEventTo']>[3];
type PushEventToReplyPayload = Parameters<NonNullable<PushEventToReply>>[0];

const MENTION_MENU_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape']);
const isMentionMenuKeyboardEvent = (event: KeyboardEvent) =>
  MENTION_MENU_KEYS.has(event.key) && Boolean(document.querySelector('.mention-menu'));

export type SuperTextProps = {
  value?: string;
  defaultValue?: string;
  onChange?: ChangeHandler;
  multiline?: boolean;
  formatting?: boolean;
  code?: boolean;
  codeLanguage?: string;
  showMenu?: boolean;
  placeholder?: string;
  placeholderVisibility?: 'always' | 'focused';
  editable?: boolean;
  autofocus?: boolean;
  escapeTemplatingBrackets?: boolean;
  fieldReferencesEnabled?: boolean;
  mentionSuggestions?: MentionSuggestion[];
  mentionAsyncLoad?: boolean;
  onInitMentions?: (currentSlugs: string[]) => Promise<MentionSuggestion[]>;
  enableSlashCommands?: boolean;
  fieldSuggestions?: FieldSuggestion[];
  fieldTypeSuggestions?: FieldTypeSuggestion[];
  onEmptyBackspace?: EventHandler;
  onEnter?: EventHandler;
  onNextEnter?: EventHandler;
  onFocus?: EventHandler;
  onBlur?: EventHandler;
  refreshOnBlur?: boolean;
  onRefreshContent?: () => string;
  fieldName?: string;
  target?: string | number;
  hiddenInputId?: string;
  externalValueSourceId?: string;
  pushEvent?: ViewHook['pushEvent'];
  pushEventTo?: ViewHook['pushEventTo'];
  bubbleMenuElementId?: string;
  className?: string;
  outputFormat?: OutputFormat;
};

const parseJsonContent = (value: string): JSONContent | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const firstChar = trimmed[0];
  if (firstChar !== '{' && firstChar !== '[') return null;
  try {
    const parsed = JSON.parse(trimmed) as JSONContent;
    if (parsed && typeof parsed === 'object' && 'type' in parsed) {
      return parsed;
    }
  } catch (_error) {
    return null;
  }
  return null;
};

const toFallbackTextFromJson = (node: JSONContent | null | undefined): string => {
  if (!node) return '';
  const chunks: string[] = [];

  const visit = (current: JSONContent | null | undefined) => {
    if (!current || typeof current !== 'object') return;

    if (current.type === 'text' && typeof current.text === 'string') {
      chunks.push(current.text);
    }

    if (current.type === 'mention' && current.attrs && typeof current.attrs.label === 'string') {
      chunks.push(current.attrs.label);
    }

    if (Array.isArray(current.content)) {
      current.content.forEach(item => visit(item as JSONContent));
    }

    if (current.type === 'paragraph' || current.type === 'heading' || current.type === 'blockquote') {
      chunks.push('\n');
    }
  };

  visit(node);
  return chunks
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const stripHtmlToText = (value: string): string => {
  if (!value) return '';
  if (!/[<>&]/.test(value)) return value;
  const container = document.createElement('div');
  container.innerHTML = value;
  return container.textContent ?? '';
};

const PROSEMIRROR_TRAILING_BREAK_RE = /<br class="ProseMirror-trailingBreak">/g;

const unwrapSingleParagraphHtml = (value: string): string => {
  const normalizedValue = value.replace(PROSEMIRROR_TRAILING_BREAK_RE, '');
  const container = document.createElement('div');
  container.innerHTML = normalizedValue;

  const meaningfulNodes = Array.from(container.childNodes).filter(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? '').trim().length > 0;
    }

    return node.nodeType === Node.ELEMENT_NODE;
  });

  if (meaningfulNodes.length !== 1) return normalizedValue;

  const [singleNode] = meaningfulNodes;
  if (!(singleNode instanceof HTMLElement) || singleNode.tagName.toLowerCase() !== 'p') return normalizedValue;

  return singleNode.innerHTML;
};

const isSingleParagraphNormalization = (currentValue: string, changedValue: string) =>
  currentValue !== changedValue && unwrapSingleParagraphHtml(currentValue) === unwrapSingleParagraphHtml(changedValue);

const shouldDeferHeavyEditorMount = ({
  serializedValue,
  formatting,
  autofocus,
  editable,
}: {
  serializedValue: string;
  formatting: boolean;
  autofocus: boolean;
  editable: boolean;
}) => editable && formatting && !autofocus && serializedValue.length > HEAVY_CONTENT_DEFER_THRESHOLD;

const SuperText = forwardRef<SuperTextHandle, SuperTextProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      multiline = false,
      formatting = false,
      showMenu = false,
      placeholder,
      placeholderVisibility = 'always',
      editable = true,
      autofocus = false,
      escapeTemplatingBrackets = false,
      fieldReferencesEnabled = false,
      mentionSuggestions = [],
      mentionAsyncLoad = false,
      onInitMentions,
      enableSlashCommands = false,
      fieldSuggestions = [],
      fieldTypeSuggestions = [],
      onEmptyBackspace,
      onEnter,
      onNextEnter,
      onFocus,
      onBlur,
      refreshOnBlur = false,
      onRefreshContent,
      fieldName = '',
      target,
      hiddenInputId,
      externalValueSourceId,
      pushEvent,
      pushEventTo,
      bubbleMenuElementId,
      className,
      outputFormat,
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
    const updateTimeoutRef = useRef<number | null>(null);
    const isApplyingExternalValueRef = useRef(false);
    const isInitialEditorMountRef = useRef(true);
    const lastValueRef = useRef<string | null>(null);
    const serializedValue = isControlled ? (value ?? '') : uncontrolledValue;
    const serializedValueRef = useRef(serializedValue);
    const externalValueVersionRef = useRef(0);
    const heightCacheKey = hiddenInputId || fieldName;
    const cachedMountHeight = heightCacheKey ? getSuperTextHeightCache()[heightCacheKey] : undefined;
    const [mentionSourceValue, setMentionSourceValue] = useState(serializedValue);
    const [editorMentionSlugs, setEditorMentionSlugs] = useState<string[]>([]);
    const currentMentionSlugs = useMemo(
      () => Array.from(new Set([...extractMentionSlugs(mentionSourceValue), ...editorMentionSlugs])).sort(),
      [mentionSourceValue, editorMentionSlugs],
    );
    const [deferHeavyContentMount, setDeferHeavyContentMount] = useState(
      () =>
        shouldDeferHeavyEditorMount({
          serializedValue,
          formatting,
          autofocus,
          editable,
        }) && cachedMountHeight == null,
    );
    const startedWithDeferredMountRef = useRef(deferHeavyContentMount);
    const pendingFocusAfterDeferredMountRef = useRef(false);
    const lastManualSlashReplayAtRef = useRef(0);
    const pendingSlashReplayTimeoutRef = useRef<number | null>(null);
    const [bubbleMenuDropdown, setBubbleMenuDropdown] = useState<
      'turnInto' | 'color' | 'more' | 'link' | 'align' | null
    >(null);
    const [linkInput, setLinkInput] = useState('');
    const linkSelectionRef = useRef<{ from: number; to: number } | null>(null);
    const colorSelectionRef = useRef<{ from: number; to: number } | null>(null);
    const bubbleMenuDropdownRef = useRef<typeof bubbleMenuDropdown>(bubbleMenuDropdown);
    const closeLinkDropdownRef = useRef<() => void>(() => undefined);
    const blockedLinkMentionQueryRef = useRef(false);
    const applyLinkInputRef = useRef<() => void>(() => undefined);
    const [selectionTick, setSelectionTick] = useState(0);
    const mentionsProviderRef = useRef<MentionsProvider | null>(null);
    if (!mentionsProviderRef.current) {
      mentionsProviderRef.current = new MentionsProvider(mentionAsyncLoad ? [] : mentionSuggestions, {
        temporal: mentionAsyncLoad,
      });
    }
    const mentionsProvider = mentionsProviderRef.current as MentionsProvider;
    const [mountMinHeight, setMountMinHeight] = useState<number | null>(() => cachedMountHeight ?? null);
    const [shouldLoadAsyncMentions, setShouldLoadAsyncMentions] = useState(false);
    const [mentionInitRetryTick, setMentionInitRetryTick] = useState(0);
    const lastLoadedMentionsKeyRef = useRef<string | null>(null);
    const mentionInitRetryCountRef = useRef<Record<string, number>>({});
    const pushEventRef = useRef(pushEvent);
    const pushEventToRef = useRef(pushEventTo);
    const hasPushEvent = typeof pushEvent === 'function';
    const hasPushEventTo = typeof pushEventTo === 'function';

    useEffect(() => {
      pushEventRef.current = pushEvent;
    }, [pushEvent]);

    useEffect(() => {
      pushEventToRef.current = pushEventTo;
    }, [pushEventTo]);

    const stablePushEvent = useCallback((...args: Parameters<NonNullable<ViewHook['pushEvent']>>) => {
      return pushEventRef.current?.(...args);
    }, []);

    const stablePushEventTo = useCallback((...args: Parameters<NonNullable<ViewHook['pushEventTo']>>) => {
      return pushEventToRef.current?.(...args);
    }, []);

    const syncEditorMentionSlugs = useCallback((doc: ProseMirrorNode) => {
      const nextSlugs = extractMentionSlugsFromDoc(doc);
      setEditorMentionSlugs(prev =>
        prev.length === nextSlugs.length && prev.every((slug, index) => slug === nextSlugs[index]) ? prev : nextSlugs,
      );
    }, []);

    const { resolveEventTarget, resolveSuperTextTarget, emitEvent, emitChange } = useLiveViewBridge({
      target,
      hiddenInputId,
      fieldName,
      pushEvent: hasPushEvent ? stablePushEvent : undefined,
      pushEventTo: hasPushEventTo ? stablePushEventTo : undefined,
    });

    const flushImmediateValue = (nextValue: string) => {
      if (updateTimeoutRef.current) {
        window.clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
      lastValueRef.current = nextValue;
      serializedValueRef.current = nextValue;
      if (!isControlled) {
        setUncontrolledValue(nextValue);
      }
      emitChange(onChange, nextValue);
      updateHiddenInputValue(nextValue, true);
    };

    const syncEditorValueImmediately = (nextEditor: Editor) => {
      const changedValue = toSerializedValue(nextEditor, resolvedOutputFormat);
      const currentValue = lastValueRef.current ?? serializedValueRef.current;

      if (currentValue === changedValue) return;
      if (changedValue === '<p></p>' && currentValue === '') {
        updateHiddenInputValue(changedValue, false);
        return;
      }
      if (isSingleParagraphNormalization(currentValue, changedValue)) {
        lastValueRef.current = changedValue;
        serializedValueRef.current = changedValue;
        updateHiddenInputValue(changedValue, false);
        return;
      }

      flushImmediateValue(changedValue);
    };

    const syncEditorValueForBlur = (nextEditor: Editor) => {
      const changedValue = toSerializedValue(nextEditor, resolvedOutputFormat);
      const currentValue = lastValueRef.current ?? serializedValueRef.current;

      if (
        currentValue !== changedValue &&
        !(changedValue === '<p></p>' && currentValue === '') &&
        !isSingleParagraphNormalization(currentValue, changedValue)
      ) {
        flushImmediateValue(changedValue);
      } else {
        lastValueRef.current = changedValue;
        serializedValueRef.current = changedValue;
        updateHiddenInputValue(changedValue, false);
      }

      return changedValue;
    };

    const resolvedPlaceholder =
      placeholder ??
      (enableSlashCommands ? DEFAULT_PLACEHOLDER : fieldReferencesEnabled ? DEFAULT_MENTION_PLACEHOLDER : '');
    const resolvedOutputFormat: OutputFormat = outputFormat ?? 'json';
    const resolvedFieldSuggestions = Array.isArray(fieldSuggestions) ? fieldSuggestions : [];
    const resolvedFieldTypeSuggestions = Array.isArray(fieldTypeSuggestions) ? fieldTypeSuggestions : [];
    const activateDeferredMount = useCallback((shouldFocus = false) => {
      if (shouldFocus) {
        pendingFocusAfterDeferredMountRef.current = true;
      }
      setDeferHeavyContentMount(prev => (prev ? false : prev));
    }, []);

    useEffect(() => {
      if (!deferHeavyContentMount) return;

      const hostWindow = window as Window & {
        requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
        cancelIdleCallback?: (id: number) => void;
      };

      let idleId: number | null = null;
      let timeoutId: number | null = null;

      const activate = () => {
        setDeferHeavyContentMount(prev => (prev ? false : prev));
      };

      if (typeof hostWindow.requestIdleCallback === 'function') {
        idleId = hostWindow.requestIdleCallback(() => activate(), { timeout: HEAVY_MOUNT_IDLE_DELAY_MS });
      } else {
        timeoutId = window.setTimeout(() => activate(), HEAVY_MOUNT_IDLE_DELAY_MS);
      }

      return () => {
        if (idleId != null && typeof hostWindow.cancelIdleCallback === 'function') {
          hostWindow.cancelIdleCallback(idleId);
        }
        if (timeoutId != null) {
          window.clearTimeout(timeoutId);
        }
      };
    }, [deferHeavyContentMount]);

    const scheduleSlashReplay = (nextEditor: Editor, delay = 0) => {
      if (pendingSlashReplayTimeoutRef.current) return;
      pendingSlashReplayTimeoutRef.current = window.setTimeout(() => {
        pendingSlashReplayTimeoutRef.current = null;
        if (!enableSlashCommands) return;
        const slashState = slashCommandsPluginKey.getState(nextEditor.state) as { active?: boolean } | null;
        if (slashState?.active) return;
        if (!hasPendingSlashTrigger(nextEditor)) return;
        const now = Date.now();
        if (now - lastManualSlashReplayAtRef.current < 120) return;
        lastManualSlashReplayAtRef.current = now;
        replaySlashTokenAtCursor(nextEditor);
      }, delay);
    };
    const getTurnIntoLabel = () => {
      void selectionTick;
      if (editor?.isActive('heading', { level: 1 })) return 'Heading 1';
      if (editor?.isActive('heading', { level: 2 })) return 'Heading 2';
      if (editor?.isActive('heading', { level: 3 })) return 'Heading 3';
      if (editor?.isActive('heading', { level: 4 })) return 'Heading 4';
      if (editor?.isActive('bulletList')) return 'Bulleted list';
      if (editor?.isActive('orderedList')) return 'Numbered list';
      if (editor?.isActive('blockquote')) return 'Blockquote';
      if (editor?.isActive('codeBlock')) return 'Code block';
      return 'Text';
    };
    const getTextAlign = () => {
      void selectionTick;
      if (editor?.isActive({ textAlign: 'center' })) return 'center';
      if (editor?.isActive({ textAlign: 'right' })) return 'right';
      if (editor?.isActive({ textAlign: 'justify' })) return 'justify';
      return 'left';
    };

    const resolveMentionsTarget = useCallback(() => resolveSuperTextTarget(), [resolveSuperTextTarget]);

    const mentionsHook = useMemo(
      () => ({
        pushEventTo: hasPushEventTo ? stablePushEventTo : undefined,
        get thisTarget() {
          return resolveMentionsTarget();
        },
      }),
      [hasPushEventTo, stablePushEventTo, resolveMentionsTarget],
    );

    const slashItems = useMemo(() => {
      if (!enableSlashCommands) return EMPTY_SLASH_ITEMS;
      return buildSlashItems({
        enableSlashCommands,
        fieldName,
        fieldSuggestions: resolvedFieldSuggestions,
        fieldTypeSuggestions: resolvedFieldTypeSuggestions,
        pushEvent: hasPushEvent ? stablePushEvent : undefined,
        pushEventTo: hasPushEventTo ? stablePushEventTo : undefined,
        getEventTarget: resolveEventTarget,
        flushImmediateValue,
      });
    }, [
      enableSlashCommands,
      resolvedFieldSuggestions,
      resolvedFieldTypeSuggestions,
      resolveEventTarget,
      hasPushEvent,
      hasPushEventTo,
      stablePushEvent,
      stablePushEventTo,
      fieldName,
    ]);

    const mentionExtension = useMemo(() => {
      if (!fieldReferencesEnabled) return null;
      return MentionExtension.build(mentionsProvider, {}, mentionsHook);
    }, [fieldReferencesEnabled, mentionsProvider, mentionsHook]);

    const linkMentionExtension = useMemo(() => {
      if (!fieldReferencesEnabled) return null;
      return MentionExtension.build(mentionsProvider, { maxMentions: 1 }, mentionsHook);
    }, [fieldReferencesEnabled, mentionsProvider, mentionsHook]);

    const slashExtension = useMemo(() => {
      if (!enableSlashCommands || !slashItems.length) return null;
      return createSlashExtension(slashItems);
    }, [enableSlashCommands, slashItems]);

    const serializeLinkInputValue = useCallback((valueEditor: Editor) => {
      let changedValue = valueEditor.getHTML();
      changedValue = MentionExtension.serializeMentions(changedValue);
      changedValue = changedValue.replace(/&nbsp;/g, ' ');
      changedValue = changedValue.replace(PROSEMIRROR_TRAILING_BREAK_RE, '');
      changedValue = changedValue.replace(/^<p>([\s\S]*?)<\/p>$/i, '$1');
      changedValue = limitSerializedMentions(changedValue, 1);

      return changedValue;
    }, []);

    const linkInputExtensions = useMemo(() => {
      const extensionsList: any[] = [Document, Paragraph, Text];

      if (linkMentionExtension) {
        extensionsList.push(linkMentionExtension);
      }

      extensionsList.push(
        Placeholder.configure({
          placeholder: LINK_INPUT_PLACEHOLDER,
          includeChildren: true,
        }),
      );

      return extensionsList;
    }, [linkMentionExtension]);

    const linkInputEditor = useEditor(
      {
        extensions: linkInputExtensions,
        content: '',
        editable: true,
        editorProps: {
          attributes: {
            class:
              'tiptap tiptap-bubble-menu__input tiptap-bubble-menu__input-editor placeholder-empty-editor text-gray-900',
          },
          handleDOMEvents: {
            keydown: (view, event) => {
              event.stopPropagation();

              const mentionRanges = getMentionNodeRanges(view.state.doc);
              const { from, to } = view.state.selection;
              const isReplacingMention = selectionOverlapsMentionNode(mentionRanges, from, to);

              if (isReplacingMention) {
                blockedLinkMentionQueryRef.current = false;
              }

              if (event.key === '@' && mentionRanges.length >= 1 && !isReplacingMention) {
                event.preventDefault();
                blockedLinkMentionQueryRef.current = true;
                return true;
              }

              if (blockedLinkMentionQueryRef.current) {
                if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Escape') {
                  blockedLinkMentionQueryRef.current = false;
                } else if (!event.metaKey && !event.ctrlKey && !event.altKey) {
                  if (/^[\p{L}\p{N}_ -]$/u.test(event.key)) {
                    event.preventDefault();
                    return true;
                  }

                  if (/^[./?#&:=]$/.test(event.key)) {
                    blockedLinkMentionQueryRef.current = false;
                  }
                }
              }

              if (isMentionMenuKeyboardEvent(event)) {
                return false;
              }

              if (event.key === 'Escape') {
                event.preventDefault();
                closeLinkDropdownRef.current();
                return true;
              }

              if (event.key === 'Enter') {
                event.preventDefault();
                applyLinkInputRef.current();
                return true;
              }

              return false;
            },
            paste: (_view, event) => {
              event.stopPropagation();
              return false;
            },
          },
        },
        onUpdate: ({ editor: updatedEditor }) => {
          const mentionRanges = getMentionNodeRanges(updatedEditor.state.doc);

          if (mentionRanges.length > 1) {
            let tr = updatedEditor.state.tr;

            mentionRanges
              .slice(1)
              .reverse()
              .forEach(({ from, to }) => {
                tr = tr.delete(from, to);
              });

            updatedEditor.view.dispatch(tr);
            return;
          }

          setLinkInput(prev => {
            const nextValue = serializeLinkInputValue(updatedEditor);
            return prev === nextValue ? prev : nextValue;
          });
        },
      },
      [linkInputExtensions, serializeLinkInputValue],
    );

    const extensions = useMemo(() => {
      const activeExtensions = formatting || multiline || enableSlashCommands ? RICH_EXTENSIONS : BASE_EXTENSIONS;
      const extensionsList = [...activeExtensions];

      if (mentionExtension) {
        extensionsList.push(mentionExtension);
      }

      extensionsList.push(
        Placeholder.configure({
          placeholder: ({ editor }) => {
            if (placeholderVisibility === 'focused' && !editor.isFocused) {
              return '';
            }
            return resolvedPlaceholder;
          },
          includeChildren: true,
        }),
      );

      if (slashExtension) {
        extensionsList.push(slashExtension);
      }

      return extensionsList;
    }, [
      formatting,
      multiline,
      enableSlashCommands,
      mentionExtension,
      placeholderVisibility,
      resolvedPlaceholder,
      slashExtension,
    ]);

    const toEditorContent = useCallback(
      (serialized: string): { content: JSONContent | string; format: OutputFormat } => {
        const json = parseJsonContent(serialized);
        if (json) return { content: json, format: 'json' };

        let content = serialized;
        if (fieldReferencesEnabled) {
          content = MentionExtension.deserializeMentions(content);
        }
        if (escapeTemplatingBrackets) {
          content = unescapeBrackets(content);
        }
        return { content, format: 'html' };
      },
      [fieldReferencesEnabled, escapeTemplatingBrackets],
    );

    const toSerializedValue = useCallback(
      (editor: Editor, format: OutputFormat) => {
        if (format === 'json') {
          return JSON.stringify(editor.getJSON());
        }

        let changedValue = editor.getHTML();
        if (escapeTemplatingBrackets) {
          changedValue = escapeBrackets(changedValue);
        }
        if (fieldReferencesEnabled) {
          changedValue = MentionExtension.serializeMentions(changedValue);
          changedValue = normalizeSerializedMentionHrefAttributes(changedValue, 1);
        }
        changedValue = changedValue.replace(/&nbsp;/g, ' ');

        if (!multiline) {
          changedValue = unwrapSingleParagraphHtml(changedValue);
        }

        return changedValue;
      },
      [escapeTemplatingBrackets, fieldReferencesEnabled, multiline],
    );

    const initialContent = useMemo(() => toEditorContent(serializedValue).content, [toEditorContent, serializedValue]);
    const fallbackPreviewText = useMemo(() => {
      const json = parseJsonContent(serializedValue);
      if (json) {
        return toFallbackTextFromJson(json);
      }

      let text = serializedValue;
      if (fieldReferencesEnabled) {
        text = MentionExtension.deserializeMentions(text);
      }
      if (escapeTemplatingBrackets) {
        text = unescapeBrackets(text);
      }
      return stripHtmlToText(text)
        .replace(/&nbsp;/g, ' ')
        .trim();
    }, [serializedValue, fieldReferencesEnabled, escapeTemplatingBrackets]);

    const editor = useEditor(
      {
        extensions,
        content: deferHeavyContentMount ? '' : initialContent,
        autofocus,
        editable,
        onBeforeCreate: () => {
          isInitialEditorMountRef.current = true;
        },
        editorProps: {
          attributes: {
            class: className ?? '',
          },
          handleClickOn: (view, _pos, node, nodePos, _event, direct) => {
            if (!direct || node.type.name !== 'horizontalRule') return false;
            if (!view.editable) return true;

            const $afterRule = view.state.doc.resolve(nodePos + node.nodeSize);
            const selectionAfterRule = TextSelection.between($afterRule, $afterRule, 1);
            view.dispatch(view.state.tr.setSelection(selectionAfterRule));
            view.focus();
            return true;
          },
          handleDOMEvents: {
            keydown: (view, event) => {
              const isInListItem = () => {
                const { $from } = view.state.selection;
                if ($from.parent.type.name === 'listItem') return true;
                const parentNode = $from.node(Math.max($from.depth - 1, 0));
                return parentNode?.type?.name === 'listItem';
              };
              const slashState = slashCommandsPluginKey.getState(view.state) as { active?: boolean } | null;
              const isSlashMenuOpen = Boolean(slashState && slashState.active);

              if (isMentionMenuKeyboardEvent(event)) {
                return false;
              }

              if (event.key === 'Backspace' && view.state.selection.empty) {
                const { doc, selection } = view.state;
                const { $from } = selection;
                const positionAfterNode =
                  $from.parentOffset === 0 && $from.depth > 0 ? doc.resolve($from.before($from.depth)) : $from;
                const nodeBefore = positionAfterNode.nodeBefore;

                if (nodeBefore?.type.name === 'horizontalRule') {
                  event.preventDefault();
                  const deleteFrom = positionAfterNode.pos - nodeBefore.nodeSize;
                  const transaction = view.state.tr.delete(deleteFrom, positionAfterNode.pos);
                  const $selectionPosition = transaction.doc.resolve(
                    Math.min(deleteFrom, transaction.doc.content.size),
                  );
                  view.dispatch(transaction.setSelection(TextSelection.near($selectionPosition, 1)));
                  return true;
                }
              }

              if (event.key === 'Backspace' && onEmptyBackspace) {
                const { doc, selection } = view.state;
                const isEmptyContent = isStructurallyEmptyDoc(doc);
                if (isEmptyContent && selection.empty) {
                  if (typeof onEmptyBackspace === 'string' && pushEvent) {
                    pushEvent(onEmptyBackspace, { name: fieldName });
                  } else {
                    emitEvent(onEmptyBackspace, { name: fieldName });
                  }
                  return true;
                }
              }

              if (event.key === 'Backspace' && view.state.selection.empty) {
                const { doc, selection } = view.state;
                const atDocStart = selection.from === 1 && selection.to === 1;
                const firstNode = doc.childCount ? doc.child(0) : null;
                if (
                  atDocStart &&
                  firstNode?.type?.name === 'paragraph' &&
                  firstNode.content.size === 0 &&
                  doc.childCount > 1
                ) {
                  view.dispatch(view.state.tr.delete(0, firstNode.nodeSize));
                  return true;
                }
              }

              if (isSlashMenuOpen && event.key === 'Enter') {
                return false;
              }

              if (event.key === 'Enter' && onEnter && !event.shiftKey) {
                event.preventDefault();
                emitEvent(onEnter, { name: fieldName });
                return true;
              }

              if (event.key === 'Enter' && onNextEnter && event.shiftKey) {
                event.preventDefault();
                emitEvent(onNextEnter, { name: fieldName });
                return true;
              }

              if (!multiline && !formatting && event.key === 'Enter' && !isInListItem()) {
                event.preventDefault();
                return true;
              }

              if (
                enableSlashCommands &&
                event.key === '/' &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey &&
                !event.isComposing
              ) {
                if (editor) {
                  scheduleSlashReplay(editor, 18);
                }
              }

              return false;
            },
          },
        },
        onUpdate: ({ editor: updatedEditor }) => {
          if (
            isApplyingExternalValueRef.current ||
            isInitialEditorMountRef.current ||
            startedWithDeferredMountRef.current
          ) {
            return;
          }

          syncEditorMentionSlugs(updatedEditor.state.doc);

          const shouldFlushEmptyValueImmediately = isStructurallyEmptyDoc(updatedEditor.state.doc);

          if (enableSlashCommands) {
            const slashState = slashCommandsPluginKey.getState(updatedEditor.state) as { active?: boolean } | null;
            const hasTransientSlashQuery = Boolean(slashState?.active || hasPendingSlashTrigger(updatedEditor));
            if (slashState?.active) {
              return;
            }

            if (hasTransientSlashQuery) {
              if (hasPendingSlashTrigger(updatedEditor)) {
                scheduleSlashReplay(updatedEditor, 0);
              }
              return;
            }
          }

          if (updateTimeoutRef.current) {
            window.clearTimeout(updateTimeoutRef.current);
          }

          if (shouldFlushEmptyValueImmediately) {
            syncEditorValueImmediately(updatedEditor);
            return;
          }

          const scheduledExternalValueVersion = externalValueVersionRef.current;
          updateTimeoutRef.current = window.setTimeout(() => {
            updateTimeoutRef.current = null;
            if (scheduledExternalValueVersion !== externalValueVersionRef.current) return;
            syncEditorValueImmediately(updatedEditor);
          }, DEBOUNCE_TIMEOUT_MS);
        },
        onBlur: ({ editor: blurEditor }) => {
          const activeElement = document.activeElement;
          if (
            bubbleMenuDropdownRef.current === 'link' &&
            activeElement?.closest('.tiptap-bubble-menu__link-editor-shell')
          ) {
            return;
          }
          const blurValue = syncEditorValueForBlur(blurEditor);
          triggerHiddenInputChange(blurValue);
          emitEvent(onBlur, { name: fieldName });
          const openSlashRequestedAt = window.__superTextOpenSlashRequestedAt?.[fieldName] ?? 0;
          const shouldKeepSlashOpenOnBlur =
            enableSlashCommands &&
            openSlashRequestedAt > 0 &&
            Date.now() - openSlashRequestedAt < 500 &&
            hasPendingSlashTrigger(blurEditor);
          if (!shouldKeepSlashOpenOnBlur) {
            closeSlashSuggestion(blurEditor, true);
          }

          if (refreshOnBlur) {
            const refreshedValue = onRefreshContent?.() ?? getHiddenInputValue();
            if (typeof refreshedValue === 'string') {
              const { content } = toEditorContent(refreshedValue);
              isApplyingExternalValueRef.current = true;
              blurEditor.commands.setContent(content);
              isApplyingExternalValueRef.current = false;
            }
          }
        },
        onFocus: () => {
          if (fieldReferencesEnabled && mentionAsyncLoad) {
            setShouldLoadAsyncMentions(true);
            setMentionInitRetryTick(tick => tick + 1);
          }
          emitEvent(onFocus, { name: fieldName });
        },
      },
      [extensions],
    );

    useEffect(() => {
      if (!editor) return;
      if (deferHeavyContentMount) return;
      if (!startedWithDeferredMountRef.current) return;

      startedWithDeferredMountRef.current = false;
      const { content } = toEditorContent(serializedValueRef.current);
      isApplyingExternalValueRef.current = true;
      editor.commands.setContent(content);
      isApplyingExternalValueRef.current = false;

      if (pendingFocusAfterDeferredMountRef.current) {
        pendingFocusAfterDeferredMountRef.current = false;
        window.requestAnimationFrame(() => {
          if (!editor.isDestroyed) {
            editor.commands.focus('end');
          }
        });
      }
    }, [editor, deferHeavyContentMount, toEditorContent]);

    useEffect(() => {
      if (!editor || deferHeavyContentMount) return;

      const animationFrameId = window.requestAnimationFrame(() => {
        isInitialEditorMountRef.current = false;
      });

      return () => window.cancelAnimationFrame(animationFrameId);
    }, [editor, deferHeavyContentMount]);

    useEffect(() => {
      if (!editor) return;
      syncEditorMentionSlugs(editor.state.doc);
    }, [editor, syncEditorMentionSlugs]);

    const { updateHiddenInputValue, getHiddenInputValue, triggerHiddenInputChange } = useHiddenInputSync({
      hiddenInputId,
      externalValueSourceId,
      editor,
      enabled: editable || Boolean(externalValueSourceId),
      isControlled,
      resolvedOutputFormat,
      toEditorContent,
      toSerializedValue,
      isApplyingExternalValueRef,
      lastValueRef,
      serializedValueRef,
      setUncontrolledValue,
      onExternalValueApplied: (nextValue, doc) => {
        setMentionSourceValue(prev => (prev === nextValue ? prev : nextValue));
        syncEditorMentionSlugs(doc);
      },
    });

    useEffect(() => {
      serializedValueRef.current = serializedValue;
      externalValueVersionRef.current += 1;
      if (updateTimeoutRef.current) {
        window.clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    }, [serializedValue]);

    useEffect(() => {
      setMentionSourceValue(serializedValue);
    }, [serializedValue]);

    useEffect(() => {
      if (!heightCacheKey) {
        setMountMinHeight(null);
        return;
      }
      setMountMinHeight(getSuperTextHeightCache()[heightCacheKey] ?? null);
    }, [heightCacheKey]);

    useEffect(() => {
      if (!editor || !heightCacheKey) return;
      const editorDom = getEditorViewSafe(editor)?.dom;
      if (!(editorDom instanceof HTMLElement)) return;

      const cache = getSuperTextHeightCache();
      let rafId: number | null = null;
      let pendingHeight: number | null = null;
      let isDisposed = false;

      const commitHeight = (rawHeight: number | null | undefined) => {
        const nextHeight = Math.round(rawHeight ?? 0);
        if (nextHeight <= 0) return;
        cache[heightCacheKey] = nextHeight;
        setMountMinHeight(prev => (prev === null ? prev : null));
      };

      const scheduleMeasure = (rawHeight?: number) => {
        if (typeof rawHeight === 'number' && Number.isFinite(rawHeight)) {
          pendingHeight = rawHeight;
        }
        if (rafId !== null) return;
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          if (isDisposed) return;

          const nextHeight = pendingHeight ?? editorDom.clientHeight;
          pendingHeight = null;
          commitHeight(nextHeight);
        });
      };

      if (typeof ResizeObserver === 'undefined') {
        scheduleMeasure();
        return () => {
          if (rafId !== null) {
            window.cancelAnimationFrame(rafId);
          }
        };
      }

      const resizeObserver = new ResizeObserver(entries => {
        const currentEntry = entries.find(entry => entry.target === editorDom);
        if (currentEntry) {
          // Defer updates outside ResizeObserver callback to avoid browser loop warnings.
          scheduleMeasure(currentEntry.contentRect.height);
          return;
        }
        scheduleMeasure();
      });
      resizeObserver.observe(editorDom);
      scheduleMeasure();

      return () => {
        isDisposed = true;
        resizeObserver.disconnect();
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
      };
    }, [editor, heightCacheKey]);

    useEffect(() => {
      if (!editor || !enableSlashCommands) return;

      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;

        const isInSlashMenu =
          target.closest('.slash-menu') ||
          target.closest('[data-theme~="slash-menu"]') ||
          target.closest('.slash-menu__search');
        if (isInSlashMenu) return;

        const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
        const menuPresent = Boolean(document.querySelector('.slash-menu'));
        const floatingPillPresent = Boolean(document.querySelector('.slash-menu__search-floating'));

        if (slashState?.active || menuPresent) {
          closeSlashSuggestion(editor, true);
          return;
        }
        if (floatingPillPresent) {
          forceRemoveStaleSlashMenuUi();
        }

        const editorDom = getEditorViewSafe(editor)?.dom;
        if (editorDom && editorDom.contains(target)) return;
      };

      document.addEventListener('pointerdown', handlePointerDown, true);

      return () => {
        document.removeEventListener('pointerdown', handlePointerDown, true);
      };
    }, [editor, enableSlashCommands]);

    useEffect(() => {
      bubbleMenuDropdownRef.current = bubbleMenuDropdown;
    }, [bubbleMenuDropdown]);

    useEffect(() => {
      if (!editor) return;
      const closeDropdowns = () => {
        if (bubbleMenuDropdownRef.current === 'link' || bubbleMenuDropdownRef.current === 'turnInto') return;
        colorSelectionRef.current = null;
        setBubbleMenuDropdown(null);
        setSelectionTick(tick => tick + 1);
      };
      editor.on('selectionUpdate', closeDropdowns);
      editor.on('blur', closeDropdowns);
      return () => {
        editor.off('selectionUpdate', closeDropdowns);
        editor.off('blur', closeDropdowns);
      };
    }, [editor]);

    useEffect(() => {
      if (!editor) return;
      const handleDocumentMouseDown = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (!target) return;
        const isInsideBubbleMenu = target instanceof Element && Boolean(target.closest('.tiptap-bubble-menu'));
        const isInsideMentionMenu = target instanceof Element && Boolean(target.closest('.mention-menu'));
        if (isInsideMentionMenu || isInsideBubbleMenu) return;
        colorSelectionRef.current = null;
        setBubbleMenuDropdown(null);

        // Clicking a bubble menu button prevents default on mousedown (so the
        // editor keeps focus), which leaves the plugin's `preventHide` flag set;
        // the flag then swallows the blur from a later click-away and the menu
        // gets stuck. Collapsing the selection on a true outside click dispatches
        // a transaction, so the plugin re-evaluates `shouldShow` and hides.
        const editorDom = getEditorViewSafe(editor)?.dom;
        if (editorDom && !editorDom.contains(target) && !editor.state.selection.empty) {
          editor.commands.setTextSelection(editor.state.selection.to);
        }
      };
      document.addEventListener('mousedown', handleDocumentMouseDown);
      return () => {
        document.removeEventListener('mousedown', handleDocumentMouseDown);
      };
    }, [editor]);

    useSlashFocusLifecycle({ editor, fieldName, enableSlashCommands });

    useEffect(() => {
      return () => {
        if (updateTimeoutRef.current) {
          window.clearTimeout(updateTimeoutRef.current);
        }
        if (pendingSlashReplayTimeoutRef.current) {
          window.clearTimeout(pendingSlashReplayTimeoutRef.current);
          pendingSlashReplayTimeoutRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      if (!linkInputEditor || bubbleMenuDropdown !== 'link') return;

      const normalizedLinkInput = limitSerializedMentions(linkInput, 1);
      if (normalizedLinkInput !== linkInput) {
        setLinkInput(normalizedLinkInput);
        return;
      }

      const currentValue = serializeLinkInputValue(linkInputEditor);
      if (currentValue !== normalizedLinkInput) {
        const content = normalizedLinkInput ? MentionExtension.deserializeMentions(normalizedLinkInput) : '';
        linkInputEditor.commands.setContent(content);
      }
    }, [bubbleMenuDropdown, linkInput, linkInputEditor, serializeLinkInputValue]);

    useEffect(() => {
      if (!linkInputEditor || bubbleMenuDropdown !== 'link') return;
      window.requestAnimationFrame(() => {
        if (!linkInputEditor.isDestroyed) {
          linkInputEditor.commands.focus('end');
        }
      });
    }, [bubbleMenuDropdown, linkInputEditor]);

    useEffect(() => {
      if (!fieldReferencesEnabled || mentionAsyncLoad) return;
      mentionsProvider.setTemporal(false);
      mentionsProvider.replaceSuggestions(mentionSuggestions);
      lastLoadedMentionsKeyRef.current = null;
    }, [fieldReferencesEnabled, mentionAsyncLoad, mentionSuggestions, mentionsProvider]);

    useEffect(() => {
      const shouldRequestMentions = shouldLoadAsyncMentions || currentMentionSlugs.length > 0;
      if (!fieldReferencesEnabled || !mentionAsyncLoad || !shouldRequestMentions) return;

      const currentSlugs = currentMentionSlugs;
      const scopeKey = String(resolveMentionsTarget() ?? target ?? hiddenInputId ?? fieldName ?? 'super-text');
      const initKey = `${scopeKey}|${currentSlugs.join(',')}`;
      if (lastLoadedMentionsKeyRef.current === initKey) {
        mentionsProvider.setTemporal(false);
        return;
      }

      mentionsProvider.setTemporal(true);
      if (mentionsProvider.suggestions.length > 0) {
        mentionsProvider.replaceSuggestions([]);
      }

      let isCancelled = false;
      let retryTimeoutId: number | null = null;

      const fetchMentions = async (): Promise<MentionSuggestion[]> => {
        if (onInitMentions) {
          return onInitMentions(currentSlugs);
        }

        if (!hasPushEventTo) {
          return [];
        }

        const targetParam = resolveMentionsTarget();
        if (!targetParam) {
          return [];
        }

        return new Promise((resolve, reject) => {
          let hasResponded = false;
          const timeoutId = window.setTimeout(() => {
            if (!hasResponded) {
              hasResponded = true;
              reject(new Error('Mentions init timed out'));
            }
          }, 5000);

          stablePushEventTo(
            targetParam,
            'init',
            { current_mention_slugs: currentSlugs },
            (reply: PushEventToReplyPayload) => {
              window.clearTimeout(timeoutId);
              if (hasResponded) return;
              hasResponded = true;

              const mentionSuggestionsRaw = reply?.mention_suggestions;
              if (typeof mentionSuggestionsRaw === 'string') {
                try {
                  const parsed = JSON.parse(mentionSuggestionsRaw);
                  resolve(Array.isArray(parsed) ? parsed : []);
                } catch (error) {
                  reject(error);
                }
              } else {
                reject(new Error('Mentions init returned invalid payload'));
              }
            },
          );
        });
      };

      const loadMentions = async () => {
        const scheduleRetry = () => {
          const retries = mentionInitRetryCountRef.current[initKey] ?? 0;
          if (!isCancelled && retries < 1) {
            mentionInitRetryCountRef.current[initKey] = retries + 1;
            retryTimeoutId = window.setTimeout(() => {
              if (!isCancelled) {
                setMentionInitRetryTick(tick => tick + 1);
              }
            }, 700);
          }
        };

        try {
          const loadedSuggestions = await loadMentionsCached(initKey, fetchMentions);
          if (isCancelled) {
            return;
          }

          const normalizedSuggestions = Array.isArray(loadedSuggestions) ? loadedSuggestions : [];
          const suggestionsForProvider = maybeApplyMockMentions(normalizedSuggestions);
          mentionsProvider.replaceSuggestions(suggestionsForProvider);

          if (suggestionsForProvider.length > 0) {
            lastLoadedMentionsKeyRef.current = initKey;
            delete mentionInitRetryCountRef.current[initKey];
            return;
          }
          scheduleRetry();
        } catch (error) {
          console.error('Failed to fetch mention suggestions:', error);
          scheduleRetry();
        } finally {
          if (!isCancelled) {
            mentionsProvider.setTemporal(false);
          }
        }
      };

      loadMentions().catch(() => undefined);

      return () => {
        isCancelled = true;
        if (retryTimeoutId != null) {
          window.clearTimeout(retryTimeoutId);
        }
      };
    }, [
      fieldReferencesEnabled,
      mentionAsyncLoad,
      shouldLoadAsyncMentions,
      currentMentionSlugs,
      mentionInitRetryTick,
      onInitMentions,
      hasPushEventTo,
      stablePushEventTo,
      target,
      hiddenInputId,
      fieldName,
      mentionsProvider,
      resolveMentionsTarget,
    ]);

    useControlledEditorSync({
      editor,
      isControlled,
      serializedValue,
      enableSlashCommands,
      toEditorContent,
      toSerializedValue,
      isApplyingExternalValueRef,
      paused: deferHeavyContentMount,
    });

    useEffect(() => {
      if (!editor || !enableSlashCommands) return;
      if (!editor.isFocused) return;

      const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
      if (slashState?.active) return;
      if (!hasPendingSlashTrigger(editor)) return;

      const now = Date.now();
      if (now - lastManualSlashReplayAtRef.current < 220) return;
      lastManualSlashReplayAtRef.current = now;
      replaySlashTokenAtCursor(editor);
    }, [editor, enableSlashCommands, serializedValue]);

    useEffect(() => {
      if (!editor) return;
      editor.setEditable(editable);
    }, [editable, editor]);

    useImperativeHandle(
      ref,
      () => ({
        focus: placement => {
          editor?.commands.focus(placement);
        },
        clearContent: () => {
          editor?.commands.clearContent();
        },
        setContent: nextValue => {
          if (!isControlled) {
            setUncontrolledValue(nextValue);
          }
          const { content } = toEditorContent(nextValue);
          editor?.commands.setContent(content);
        },
        getEditor: () => editor,
      }),
      [editor, isControlled],
    );

    const normalizeLink = (value: string) => {
      const trimmed = limitSerializedMentions(MentionExtension.serializeMentions(value), 1).trim();
      if (!trimmed) return '';
      if (MentionExtension.hasSerializedMentions(trimmed)) return trimmed;
      if (/^(#|\/|\.\/|\.\.\/)/.test(trimmed)) return trimmed;
      if (/^(https?:\/\/|mailto:|tel:|slack:\/\/)/i.test(trimmed)) return trimmed;
      if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) return `mailto:${trimmed}`;
      return `https://${trimmed}`;
    };

    const renderColorOption = (
      item: { name: string; color: string | null },
      onSelect: (color: string | null) => void,
      swatch: { border?: string; fallback?: string } = {},
    ) => (
      <button
        key={item.name}
        type='button'
        className='tiptap-bubble-menu__dropdown-item'
        onMouseDown={event => event.preventDefault()}
        onClick={() => {
          onSelect(item.color);
          setBubbleMenuDropdown(null);
        }}
      >
        <span
          className='tiptap-bubble-menu__color-swatch'
          style={{
            backgroundColor: item.color ?? swatch.fallback ?? '#111827',
            border: swatch.border,
          }}
        />
        {item.name}
      </button>
    );

    const renderColorSection = (
      title: string,
      items: { name: string; color: string | null }[],
      onSelect: (color: string | null) => void,
      swatch?: { border?: string; fallback?: string },
    ) => (
      <>
        <div className='tiptap-bubble-menu__dropdown-title'>{title}</div>
        {items.map(item => renderColorOption(item, onSelect, swatch))}
      </>
    );

    if (!editor || deferHeavyContentMount) {
      const cachedHeight = heightCacheKey ? getSuperTextHeightCache()[heightCacheKey] : undefined;
      const fallbackMinHeight = mountMinHeight ?? cachedHeight ?? (multiline ? 100 : 40);
      const fallbackText = fallbackPreviewText || (!editor ? '' : resolvedPlaceholder);
      return (
        <div
          className={className}
          data-bubble-menu-id={bubbleMenuElementId}
          style={{ minHeight: `${fallbackMinHeight}px` }}
          onMouseDown={() => activateDeferredMount(true)}
          onTouchStart={() => activateDeferredMount(true)}
          onFocus={() => activateDeferredMount(true)}
          tabIndex={deferHeavyContentMount && editable ? 0 : undefined}
        >
          <span className='whitespace-pre-wrap'>{fallbackText}</span>
        </div>
      );
    }

    const closeLinkDropdown = () => {
      linkSelectionRef.current = null;
      bubbleMenuDropdownRef.current = null;
      setBubbleMenuDropdown(null);
    };

    const rememberCurrentLinkSelection = () => {
      const { from, to } = editor.state.selection;
      linkSelectionRef.current = { from, to };
    };

    const rememberCurrentColorSelection = () => {
      const { from, to } = editor.state.selection;
      if (from !== to) {
        colorSelectionRef.current = { from, to };
      }
    };

    const getColorCommandChain = () => {
      const chain = editor.chain().focus();
      const savedSelection = colorSelectionRef.current;
      if (savedSelection && savedSelection.from !== savedSelection.to) {
        return chain.setTextSelection(savedSelection);
      }
      return chain;
    };

    const clearColorSelection = () => {
      colorSelectionRef.current = null;
    };

    // Mark/node commands don't change React state, so the toolbar (active
    // states, "Turn into" label) would render stale without an explicit tick.
    const runToolbarCommand = (command: () => unknown) => {
      command();
      setSelectionTick(tick => tick + 1);
    };

    const applyLinkInput = () => {
      const rawLinkInput = linkInputEditor ? serializeLinkInputValue(linkInputEditor) : linkInput;
      const normalized = normalizeLink(rawLinkInput);
      const savedSelection = linkSelectionRef.current;
      const hasSavedRange = Boolean(savedSelection && savedSelection.from !== savedSelection.to);
      if (!normalized) {
        if (hasSavedRange && savedSelection) {
          editor.chain().focus().setTextSelection(savedSelection).unsetLink().run();
        } else {
          editor.chain().focus().unsetLink().run();
        }
      } else {
        if (hasSavedRange && savedSelection) {
          editor.chain().focus().setTextSelection(savedSelection).setLink({ href: normalized }).run();
        } else {
          editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
        }
      }
      closeLinkDropdown();
    };

    closeLinkDropdownRef.current = closeLinkDropdown;
    applyLinkInputRef.current = applyLinkInput;

    const showBubbleMenu = Boolean(showMenu && formatting);

    return (
      <>
        {showBubbleMenu && (
          <BubbleMenu
            editor={editor}
            shouldShow={({ editor: currentEditor }: { editor: Editor }) => {
              const selection = currentEditor.state.selection;
              const doc = currentEditor.state.doc;
              const isLinkEditorFocused =
                bubbleMenuDropdownRef.current === 'link' &&
                Boolean(document.activeElement?.closest('.tiptap-bubble-menu__link-editor-shell'));
              const isHorizontalRuleSelection =
                selection instanceof NodeSelection && selection.node.type.name === 'horizontalRule';
              return (
                (currentEditor.isFocused || isLinkEditorFocused) &&
                !isHorizontalRuleSelection &&
                !selection.empty &&
                doc.childCount > 0 &&
                doc.content.size > 2
              );
            }}
            appendTo={() => document.body}
            options={{ placement: 'top', arrow: false, strategy: 'fixed' }}
            className='tiptap-bubble-menu'
          >
            <div className='tiptap-bubble-menu__content'>
              <div className='tiptap-bubble-menu__dropdown'>
                <button
                  type='button'
                  className='tiptap-bubble-menu__btn tiptap-bubble-menu__btn--pill'
                  aria-label='Turn into'
                  data-tooltip='Turn into'
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => setBubbleMenuDropdown(current => (current === 'turnInto' ? null : 'turnInto'))}
                >
                  <span className='tiptap-bubble-menu__pill-icon'>
                    <HeadingIcon />
                  </span>
                  {getTurnIntoLabel()}
                  <span className='tiptap-bubble-menu__chevron'>▾</span>
                </button>
                {bubbleMenuDropdown === 'turnInto' && (
                  <div className='tiptap-bubble-menu__dropdown-menu'>
                    <div className='tiptap-bubble-menu__dropdown-title'>Turn into</div>
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        editor.chain().focus().setParagraph().run();
                        setBubbleMenuDropdown(null);
                      }}
                    >
                      <span className='tiptap-bubble-menu__dropdown-icon'>
                        <TextIcon />
                      </span>
                      Text
                    </button>
                    {([1, 2, 3, 4] as const).map(level => (
                      <button
                        key={level}
                        type='button'
                        className='tiptap-bubble-menu__dropdown-item'
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => {
                          editor.chain().focus().toggleHeading({ level }).run();
                          setBubbleMenuDropdown(null);
                        }}
                      >
                        <span className='tiptap-bubble-menu__dropdown-icon'>
                          {level === 1 && <Heading1Icon />}
                          {level === 2 && <Heading2Icon />}
                          {level === 3 && <Heading3Icon />}
                          {level === 4 && <Heading4Icon />}
                        </span>
                        Heading {level}
                      </button>
                    ))}
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        editor.chain().focus().toggleBulletList().run();
                        setBubbleMenuDropdown(null);
                      }}
                    >
                      <span className='tiptap-bubble-menu__dropdown-icon'>
                        <BulletListIcon />
                      </span>
                      Bulleted list
                    </button>
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        editor.chain().focus().toggleOrderedList().run();
                        setBubbleMenuDropdown(null);
                      }}
                    >
                      <span className='tiptap-bubble-menu__dropdown-icon'>
                        <OrderedListIcon />
                      </span>
                      Numbered list
                    </button>
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        editor.chain().focus().toggleBlockquote().run();
                        setBubbleMenuDropdown(null);
                      }}
                    >
                      <span className='tiptap-bubble-menu__dropdown-icon'>
                        <QuoteIcon />
                      </span>
                      Blockquote
                    </button>
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        editor.chain().focus().toggleCodeBlock().run();
                        setBubbleMenuDropdown(null);
                      }}
                    >
                      <span className='tiptap-bubble-menu__dropdown-icon'>
                        <CodeBlockIcon />
                      </span>
                      Code block
                    </button>
                  </div>
                )}
              </div>
              <span className='tiptap-bubble-menu__divider' />
              <button
                type='button'
                className={['tiptap-bubble-menu__btn', editor.isActive('bold') && 'is-active']
                  .filter(Boolean)
                  .join(' ')}
                aria-label='Bold'
                data-tooltip='Bold'
                onMouseDown={event => event.preventDefault()}
                onClick={() => runToolbarCommand(() => editor.chain().focus().toggleBold().run())}
              >
                <BoldIcon />
              </button>
              <button
                type='button'
                className={['tiptap-bubble-menu__btn', 'is-italic', editor.isActive('italic') && 'is-active']
                  .filter(Boolean)
                  .join(' ')}
                aria-label='Italic'
                data-tooltip='Italic'
                onMouseDown={event => event.preventDefault()}
                onClick={() => runToolbarCommand(() => editor.chain().focus().toggleItalic().run())}
              >
                <ItalicIcon />
              </button>
              <button
                type='button'
                className={['tiptap-bubble-menu__btn', editor.isActive('underline') && 'is-active']
                  .filter(Boolean)
                  .join(' ')}
                aria-label='Underline'
                data-tooltip='Underline'
                onMouseDown={event => event.preventDefault()}
                onClick={() => runToolbarCommand(() => editor.chain().focus().toggleUnderline().run())}
              >
                <UnderlineIcon />
              </button>
              <button
                type='button'
                className={['tiptap-bubble-menu__btn', editor.isActive('strike') && 'is-active']
                  .filter(Boolean)
                  .join(' ')}
                aria-label='Strikethrough'
                data-tooltip='Strikethrough'
                onMouseDown={event => event.preventDefault()}
                onClick={() => runToolbarCommand(() => editor.chain().focus().toggleStrike().run())}
              >
                <StrikethroughIcon />
              </button>
              <div className='tiptap-bubble-menu__dropdown'>
                <button
                  type='button'
                  className='tiptap-bubble-menu__btn'
                  aria-label='Align'
                  data-tooltip='Align'
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => setBubbleMenuDropdown(current => (current === 'align' ? null : 'align'))}
                >
                  <AlignIcon align={getTextAlign()} />
                  <span className='tiptap-bubble-menu__chevron'>▾</span>
                </button>
                {bubbleMenuDropdown === 'align' && (
                  <div className='tiptap-bubble-menu__dropdown-menu'>
                    <div className='tiptap-bubble-menu__dropdown-title'>Align</div>
                    {TEXT_ALIGN_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type='button'
                        className={['tiptap-bubble-menu__dropdown-item', getTextAlign() === option.value && 'is-active']
                          .filter(Boolean)
                          .join(' ')}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => {
                          runToolbarCommand(() => editor.chain().focus().setTextAlign(option.value).run());
                          setBubbleMenuDropdown(null);
                        }}
                      >
                        <span className='tiptap-bubble-menu__dropdown-icon'>
                          <AlignIcon align={option.value} />
                        </span>
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className='tiptap-bubble-menu__divider' />
              <button
                type='button'
                className={['tiptap-bubble-menu__btn', editor.isActive('link') && 'is-active']
                  .filter(Boolean)
                  .join(' ')}
                aria-label='Link'
                data-tooltip='Link'
                onMouseDown={event => {
                  rememberCurrentLinkSelection();
                  event.preventDefault();
                }}
                onClick={() =>
                  setBubbleMenuDropdown(current => {
                    const next = current === 'link' ? null : 'link';
                    bubbleMenuDropdownRef.current = next;
                    if (next === 'link') {
                      const currentUrl = editor.getAttributes('link').href as string | undefined;
                      setLinkInput(
                        limitSerializedMentions(MentionExtension.serializeMentions(currentUrl ?? '').trim(), 1),
                      );
                    }
                    return next;
                  })
                }
              >
                <LinkIcon />
              </button>
              {bubbleMenuDropdown === 'link' && (
                <div className='tiptap-bubble-menu__dropdown-menu tiptap-bubble-menu__dropdown-menu--link'>
                  <div className='tiptap-bubble-menu__dropdown-title'>Link</div>
                  <div className='tiptap-bubble-menu__link-editor-shell'>
                    <EditorContent editor={linkInputEditor} />
                  </div>
                  <div className='tiptap-bubble-menu__dropdown-actions'>
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        const savedSelection = linkSelectionRef.current;
                        const hasSavedRange = Boolean(savedSelection && savedSelection.from !== savedSelection.to);
                        if (hasSavedRange && savedSelection) {
                          editor.chain().focus().setTextSelection(savedSelection).unsetLink().run();
                        } else {
                          editor.chain().focus().unsetLink().run();
                        }
                        closeLinkDropdown();
                      }}
                    >
                      Remove link
                    </button>
                    <button
                      type='button'
                      className='tiptap-bubble-menu__dropdown-item'
                      onMouseDown={event => event.preventDefault()}
                      onClick={applyLinkInput}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
              <div className='tiptap-bubble-menu__dropdown'>
                <button
                  type='button'
                  className='tiptap-bubble-menu__btn tiptap-bubble-menu__btn--circle'
                  aria-label='Text color'
                  data-tooltip='Text color'
                  onMouseDown={event => {
                    rememberCurrentColorSelection();
                    event.preventDefault();
                  }}
                  onClick={() =>
                    setBubbleMenuDropdown(current => {
                      const next = current === 'color' ? null : 'color';
                      bubbleMenuDropdownRef.current = next;
                      if (next !== 'color') {
                        clearColorSelection();
                      }
                      return next;
                    })
                  }
                >
                  <span
                    className='tiptap-bubble-menu__color-swatch tiptap-bubble-menu__color-swatch--current'
                    style={{
                      backgroundColor: editor.getAttributes('textStyle').color || '#111827',
                      border: editor.getAttributes('textStyle').color ? 'none' : '1px solid #e5e7eb',
                    }}
                  />
                  <span className='tiptap-bubble-menu__chevron'>▾</span>
                </button>
                {bubbleMenuDropdown === 'color' && (
                  <div className='tiptap-bubble-menu__dropdown-menu tiptap-bubble-menu__dropdown-menu--colors'>
                    {renderColorSection('Color', TEXT_COLOR_OPTIONS, color => {
                      if (color) {
                        getColorCommandChain().setColor(color).run();
                      } else {
                        getColorCommandChain().unsetColor().run();
                      }
                      clearColorSelection();
                    })}
                    {renderColorSection(
                      'Highlight',
                      HIGHLIGHT_COLOR_OPTIONS,
                      color => {
                        if (color) {
                          getColorCommandChain().setHighlight({ color }).run();
                        } else {
                          getColorCommandChain().unsetHighlight().run();
                        }
                        clearColorSelection();
                      },
                      { border: '1px solid #e5e7eb', fallback: 'transparent' },
                    )}
                  </div>
                )}
              </div>
              <button
                type='button'
                className='tiptap-bubble-menu__btn tiptap-bubble-menu__btn--circle'
                aria-label='Clear formatting'
                data-tooltip='Clear formatting'
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  runToolbarCommand(() => editor.chain().focus().unsetAllMarks().clearNodes().unsetTextAlign().run());
                  setBubbleMenuDropdown(null);
                }}
              >
                <ClearFormattingIcon />
              </button>
            </div>
          </BubbleMenu>
        )}

        <EditorContent
          editor={editor}
          data-bubble-menu-id={bubbleMenuElementId}
          style={mountMinHeight ? { minHeight: `${mountMinHeight}px` } : undefined}
        />
      </>
    );
  },
);

SuperText.displayName = 'SuperText';

export default SuperText;
